import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "dialflow_cart_v1";
const CartContext = createContext(null);

/**
 * Simple client-side cart persisted to localStorage.
 * Shape: { [itemId]: { id, name, price, image, quantity } }
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  const add = useCallback((item, delta = 1) => {
    setItems((prev) => {
      const existing = prev[item.id];
      const nextQty = Math.max(0, (existing?.quantity || 0) + delta);
      if (nextQty === 0) {
        const copy = { ...prev };
        delete copy[item.id];
        return copy;
      }
      return {
        ...prev,
        [item.id]: {
          id: item.id,
          name: item.name,
          price: item.price,
          image: (item.images && item.images[0]) || existing?.image || null,
          quantity: nextQty,
        },
      };
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => { const c = { ...prev }; delete c[id]; return c; });
  }, []);

  const clear = useCallback(() => setItems({}), []);

  const list = useMemo(() => Object.values(items), [items]);
  const subtotal = useMemo(
    () => list.reduce((s, i) => s + (Number(i.price) || 0) * (i.quantity || 0), 0),
    [list]
  );
  const count = useMemo(() => list.reduce((s, i) => s + (i.quantity || 0), 0), [list]);

  const value = { items, list, subtotal, count, add, remove, clear };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
